import { ClockIcon } from '@langchain/untitled-ui-icons';

import { useLayoutEffect, useRef, useState } from 'react';

import { usePrevious } from '@/hooks/usePrevious';
import { getLatencyTheme } from '@/utils/latencyTheme';
import { cn } from '@/utils/tailwind';

export function PlaygroundLatency(props: {
  startDate?: Date;
  loadingState?: 'loading' | 'done';
  size?: 'xs' | 'sm';
  className?: string;
}) {
  const [latencyTheme, setLatencyTheme] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const prevStartDate = usePrevious(props.startDate);

  useLayoutEffect(() => {
    let frameCb = 0;

    function frame() {
      if (props.loadingState === 'loading') {
        frameCb = requestAnimationFrame(frame);
      }
      const value = props.startDate?.valueOf();
      const prevValue = prevStartDate?.valueOf();
      if (value == null || value === prevValue) return;

      if (ref.current != null) {
        const latencySec = (Date.now() - value) / 1000;

        // only an approximative value, as the duration
        // is measured locally, instead of the time of the
        // playground evaluator
        ref.current.innerText = `≈${latencySec.toFixed(2)}s`;

        // as string is a primitve value, it will not trigger
        // a rerender unless the value differs
        setLatencyTheme(getLatencyTheme(latencySec) ?? '');
      }
    }

    frame();
    return () => cancelAnimationFrame(frameCb);
  }, [props.startDate, props.loadingState]);

  const size = props.size ?? 'xs';
  if (props.startDate == null) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md border-transparent',
        latencyTheme,
        {
          'px-1 py-0.5 text-xs': size === 'xs',
          'px-1 py-0 text-sm': size === 'sm',
        },
        props.className
      )}
    >
      <ClockIcon className="size-4" />
      <div ref={ref} />
    </div>
  );
}
